import {
  queryOptions,
  useQuery,
  type QueryClient,
} from '@tanstack/react-query';
import { fetchSanApi } from '@/modules/fetch-san-api';

type LogoSpec = { ticker: string; slug?: string | undefined };

// The per-key resolution: the asset's Santiment slug (null when SanAPI has
// no sluggable project for the key — or when the caller keyed by slug, which
// needs no resolving) and its hosted logo (null when no project has one).
type AssetProject = { slug: string | null; logoUrl: string | null };

function logoKeyOf(spec: LogoSpec): string {
  return spec.slug != null ?
      `slug:${spec.slug}`
    : `ticker:${spec.ticker.toUpperCase()}`;
}

type Waiter = {
  resolve: (project: AssetProject) => void;
  reject: (err: unknown) => void;
};

const queue = new Map<string, { spec: LogoSpec; waiters: Waiter[] }>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_MS = 25;

function requestProject(spec: LogoSpec): Promise<AssetProject> {
  return new Promise((resolve, reject) => {
    const key = logoKeyOf(spec);
    const entry = queue.get(key) ?? { spec, waiters: [] };
    entry.waiters.push({ resolve, reject });
    queue.set(key, entry);
    flushTimer ??= setTimeout(() => {
      void flush();
    }, FLUSH_MS);
  });
}

async function flush() {
  flushTimer = null;
  const batch = [...queue.values()];
  queue.clear();
  // Stryker disable next-line ConditionalExpression: defensive — the timer is only ever armed by an enqueue, so a flush never sees an empty queue; kept so a future direct flush() call can't fire an empty "{}" query
  if (batch.length === 0) return;
  const aliases = batch.map(({ spec }, i) =>
    spec.slug != null ?
      `a${i}:projectBySlug(slug:${JSON.stringify(spec.slug)}){logoUrl}`
    : `a${i}:allProjectsByTicker(ticker:${JSON.stringify(spec.ticker.toUpperCase())}){slug logoUrl marketcapUsd}`,
  );
  // Stryker disable next-line StringLiteral: equivalent — GraphQL selections need no separator ('' joins into equally valid syntax); \n is readability for logged queries
  const body = aliases.join('\n');
  try {
    // Per-alias errors (e.g. unknown slug) null that alias and are expected —
    // only a whole-request failure rejects the waiters.
    const { data } = await fetchSanApi<Record<string, unknown>>(`{${body}}`);
    batch.forEach(({ waiters }, i) => {
      const project = pickProject(data[`a${i}`]);
      waiters.forEach((w) => w.resolve(project));
    });
  } catch (err) {
    batch.forEach(({ waiters }) => waiters.forEach((w) => w.reject(err)));
  }
}

// Ticker lookups pick per field from the allProjectsByTicker array: the
// logo is the highest-marketcap project that HAS a logo, the slug the
// highest-marketcap project that HAS a slug — each field independently
// canonical for shared tickers.
function pickProject(node: unknown): AssetProject {
  if (Array.isArray(node)) {
    const items: unknown[] = node;
    let bestLogo: { logoUrl: string; cap: number } | null = null;
    let bestSlug: { slug: string; cap: number } | null = null;
    for (const p of items) {
      if (typeof p !== 'object' || p === null) continue;
      const capRaw = 'marketcapUsd' in p ? p.marketcapUsd : undefined;
      const cap = typeof capRaw === 'number' ? capRaw : -1;
      const logoUrl = 'logoUrl' in p ? p.logoUrl : undefined;
      if (
        typeof logoUrl === 'string' &&
        logoUrl !== '' &&
        (bestLogo === null || cap > bestLogo.cap)
      ) {
        bestLogo = { logoUrl, cap };
      }
      const slug = 'slug' in p ? p.slug : undefined;
      if (
        typeof slug === 'string' &&
        slug !== '' &&
        (bestSlug === null || cap > bestSlug.cap)
      ) {
        bestSlug = { slug, cap };
      }
    }
    return {
      slug: bestSlug?.slug ?? null,
      logoUrl: bestLogo?.logoUrl ?? null,
    };
  }
  if (node !== null && typeof node === 'object' && 'logoUrl' in node) {
    const url = node.logoUrl;
    return {
      slug: null,
      logoUrl: typeof url === 'string' && url !== '' ? url : null,
    };
  }
  return { slug: null, logoUrl: null };
}

// react-query is the ONE session cache: projects are global truth, not
// per-user state, so entries never go stale and no auth-lifecycle reset
// applies. A failed batch leaves no data, so the next call refetches instead
// of pinning the failure.
function projectQueryOptions(spec: LogoSpec) {
  return queryOptions({
    queryKey: ['asset-logo', logoKeyOf(spec)],
    queryFn: () => requestProject(spec),
    staleTime: Infinity,
    retry: false,
  });
}

// Santiment-hosted coin logos resolved live from SanAPI — for callers that
// only have a bare ticker, or whose server-provided logo URL can be a dead
// guess for assets the backend doesn't know. Keyed by slug when the caller
// has one (exact projectBySlug lookup), else by UPPERCASED ticker via
// allProjectsByTicker's highest-marketcap pick — the canonical asset for
// shared tickers (verified live 2026-08-12: PUMP → pump.fun over pumpBTC,
// BTC → bitcoin over the empty "btc" project) — self-maintaining, no
// curated ticker map to drift.
//
// Batching: lookups arriving within one flush window (FLUSH_MS) merge into
// ONE aliased GraphQL call. A per-alias server error (unknown asset) nulls
// only that alias; a whole-batch failure settles EVERY waiter as "no logo" —
// never a hanging promise (logos are cosmetic, the caller's fallback chain
// takes over).
export function useAssetLogo(spec: {
  ticker: string;
  slug?: string | null;
  enabled: boolean; // false → no fetch is ever enqueued, resolving stays false
}): {
  // null while unresolved or when SanAPI has no logo for the asset.
  url: string | null;
  // true only while an enabled lookup is in flight — callers hold cheaper
  // fallbacks (placeholder badge, not a doomed CDN request) until it settles.
  resolving: boolean;
} {
  const slug = spec.slug ?? undefined;
  const { data, isPending } = useQuery({
    ...projectQueryOptions({ ticker: spec.ticker, slug }),
    enabled: spec.enabled,
  });
  return { url: data?.logoUrl ?? null, resolving: spec.enabled && isPending };
}

// Ticker → Santiment slug through the same batch, cache entry, and
// canonical-pick as the logo face — for non-hook callers (query functions)
// that chart by slug. null = SanAPI knows no sluggable project for the
// ticker; a whole-batch failure REJECTS (unlike the logo face's null) so
// the caller's own retry policy re-resolves.
export function getTickerSlug(
  queryClient: QueryClient,
  ticker: string,
): Promise<string | null> {
  return queryClient
    .fetchQuery(projectQueryOptions({ ticker }))
    .then((project) => project.slug);
}
